import React                      from 'react';
import antdTheme                  from 'antd/lib/theme';
import View                       from '@widesk-ui/components/View';
import { doubtKey }               from '@/TokamapEditor/helpers/planDoubts';
import type { PlanArbitration }   from '@/TokamapEditor/helpers/planDoubts';
import type { PlanShapeState }    from '@/TokamapEditor/helpers/planShapes';
import type { PlanShapeStyle }    from '@/TokamapEditor/helpers/planShapes';
import { usePlanViewport }        from '@/TokamapEditor/helpers/usePlanViewport';
import type { PlanView }          from '@/TokamapEditor/helpers/usePlanViewport';
import PlanZoomControls           from '@/TokamapEditor/ux/PlanZoomControls';
import type { PlanRegion }        from '@models/map/dxfTypes';
import type { PlanShape }         from '@models/map/dxfTypes';
import type { PlanSourceResult }  from '@models/map/dxfTypes';

export type PlanShapeEntry = {
	shape: PlanShape;
	key: string;
	enabled: boolean;
	state: PlanShapeState;
	style: PlanShapeStyle;
};

type Props = {
	source: PlanSourceResult;
	entries: PlanShapeEntry[];
	current: Pick<PlanShape, 'layer' | 'index'> | null;
	doubts: PlanDoubtBadge[];
	focusRequest: number;
	isolated: PlanShapeState | null;
	frameRequest: { keys: string[]; nonce: number } | null;
	highlighted: Set<string>;
	onArbitrate: (shape: PlanShape, decision: PlanArbitration) => void;
	onGoDoubt: (index: number) => void;
};

export type PlanDoubtBadge = {
	key: string;
	index: number;
	number: number;
	settled: boolean;
};

const FOCUS_MARGIN = 3;
const REGION_MARGIN = 0.04;

function openingView(source: PlanSourceResult): PlanView {
	const region = source.analysisRegion;
	if (!region) return { x: 0, y: 0, w: source.width, h: source.height };

	const width = region.maxX - region.minX;
	const height = region.maxY - region.minY;

	return {
		x: region.minX - width * REGION_MARGIN,
		y: region.minY - height * REGION_MARGIN,
		w: width * (1 + 2 * REGION_MARGIN),
		h: height * (1 + 2 * REGION_MARGIN),
	};
}

function veilPath(source: PlanSourceResult, region: PlanRegion): string {
	return `M0,0 H${source.width} V${source.height} H0 Z`
		+ ` M${region.minX},${region.minY} H${region.maxX} V${region.maxY} H${region.minX} Z`;
}

function topCenter(shape: PlanShape): [number, number] {
	const xs = shape.points.map(point => point[0]);
	const ys = shape.points.map(point => point[1]);

	return [(Math.min(...xs) + Math.max(...xs)) / 2, Math.min(...ys)];
}

function pointsOf(shape: PlanShape): string {
	return shape.points.map(point => `${point[0]},${point[1]}`).join(' ');
}

const PlanShapesCanvas = ({
	source, entries, current, doubts, focusRequest, isolated, frameRequest, highlighted,
	onArbitrate, onGoDoubt,
}: Props) => {
	const { token } = antdTheme.useToken();
	const viewport = usePlanViewport(source, () => openingView(source));
	const { view, setView } = viewport;
	const [screen, setScreen] = React.useState<{ width: number; height: number } | null>(null);
	const pixel = screen?.width && screen.height ? Math.max(view.w / screen.width, view.h / screen.height) : view.w / source.width;

	React.useEffect(() => {
		const svg = viewport.svgRef.current;
		if (!svg) return;

		const observer = new ResizeObserver(([entry]) => {
			if (entry) setScreen({ width: entry.contentRect.width, height: entry.contentRect.height });
		});
		observer.observe(svg);

		return () => observer.disconnect();
	}, []);

	const currentEntry = current ? entries.find(entry => entry.key === doubtKey(current)) : undefined;
	const entryByKey = React.useMemo(() => new Map(entries.map(entry => [entry.key, entry])), [entries]);

	React.useEffect(() => {
		if (!currentEntry) return;

		const { shape } = currentEntry;
		const xs = shape.points.map(point => point[0]);
		const ys = shape.points.map(point => point[1]);
		const size = Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys), source.width / 100)
			* (1 + 2 * FOCUS_MARGIN);

		setView({ x: shape.centroid[0] - size / 2, y: shape.centroid[1] - size / 2, w: size, h: size });
	}, [currentEntry?.key, focusRequest]);

	React.useEffect(() => {
		const points = (frameRequest?.keys ?? []).flatMap(key => entryByKey.get(key)?.shape.points ?? []);
		if (!points.length) return;

		const xs = points.map(point => point[0]);
		const ys = points.map(point => point[1]);
		const width = Math.max(...xs) - Math.min(...xs);
		const height = Math.max(...ys) - Math.min(...ys);
		const margin = Math.max(width, height, source.width / 50) * 0.1;

		setView({ x: Math.min(...xs) - margin, y: Math.min(...ys) - margin, w: width + 2 * margin, h: height + 2 * margin });
	}, [frameRequest?.nonce]);

	return (
		<View flex style={{ position: 'relative', background: token.colorWhite, overflow: 'hidden' }}>
			<svg
				ref={viewport.svgRef}
				viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
				style={{ width: '100%', height: '100%', cursor: viewport.panning ? 'grabbing' : 'grab', touchAction: 'none' }}
				onWheel={viewport.onWheel}
				onPointerDown={viewport.startPan}
				onPointerMove={viewport.movePan}
				onPointerUp={viewport.endPan}
				onPointerLeave={viewport.endPan}
			>
				<image href={source.imageDataUri} x={0} y={0} width={source.width} height={source.height} />

				{source.analysisRegion && (
					<path
						d={veilPath(source, source.analysisRegion)}
						fillRule="evenodd"
						fill={token.colorWhite}
						fillOpacity={0.72}
						pointerEvents="none"
					/>
				)}

				{entries.map(({ shape, key, enabled, state, style }) => {
					const stroke = style.borderWidth * pixel;
					const dimmed = isolated
						? state !== isolated
						: !!currentEntry && 'doubtful' === state && key !== currentEntry.key;

					return (
						<polygon
							key={key}
							points={pointsOf(shape)}
							fill={style.bgColor}
							stroke={highlighted.has(key) ? token.colorPrimary : style.borderColor}
							strokeWidth={highlighted.has(key) ? stroke * 2 : stroke}
							strokeDasharray={style.borderDashed ? `${stroke * 3},${stroke * 2}` : undefined}
							style={{ cursor: 'pointer', opacity: dimmed ? (isolated ? 0.12 : 0.4) : 1 }}
							onPointerDown={event => event.stopPropagation()}
							onClick={() => onArbitrate(shape, enabled ? 'discarded' : 'kept')}
						>
							<title>{enabled ? 'Cliquer pour écarter' : 'Cliquer pour garder'}</title>
						</polygon>
					);
				})}

				{currentEntry && (
					<g pointerEvents="none">
						<polygon
							points={pointsOf(currentEntry.shape)}
							fill="none"
							stroke={token.colorPrimary}
							strokeLinejoin="round"
						>
							<animate attributeName="stroke-width" values={`${4 * pixel};${22 * pixel}`} dur="1.4s" repeatCount="indefinite" />
							<animate attributeName="stroke-opacity" values="0.55;0" dur="1.4s" repeatCount="indefinite" />
						</polygon>
						<polygon
							points={pointsOf(currentEntry.shape)}
							fill={token.colorPrimary}
							fillOpacity={0.4}
							stroke={token.colorWhite}
							strokeWidth={6 * pixel}
							strokeLinejoin="round"
						/>
						<polygon
							points={pointsOf(currentEntry.shape)}
							fill="none"
							stroke={token.colorPrimary}
							strokeWidth={3 * pixel}
							strokeLinejoin="round"
						/>
					</g>
				)}

				{doubts.map(doubt => {
					const entry = entryByKey.get(doubt.key);
					if (!entry) return null;

					const isCurrent = entry.key === currentEntry?.key;
					const [x, y] = topCenter(entry.shape);
					const radius = (isCurrent ? 13 : 10) * pixel;
					const fill = isCurrent ? token.colorPrimary : doubt.settled ? token.colorTextQuaternary : token.colorWarning;

					return (
						<g
							key={doubt.key}
							style={{ cursor: 'pointer' }}
							onPointerDown={event => event.stopPropagation()}
							onClick={() => onGoDoubt(doubt.index)}
						>
							<circle cx={x} cy={y - radius - 3 * pixel} r={radius} fill={fill} stroke={token.colorWhite} strokeWidth={2 * pixel} />
							<text
								x={x}
								y={y - radius - 3 * pixel}
								textAnchor="middle"
								dominantBaseline="central"
								fill={token.colorWhite}
								style={{ fontSize: (isCurrent ? 13 : 11) * pixel, fontWeight: 600, userSelect: 'none' }}
							>
								{doubt.number}
							</text>
							<title>{`Doute n° ${doubt.number}`}</title>
						</g>
					);
				})}
			</svg>

			<PlanZoomControls
				zoomPercent={viewport.zoomPercent}
				onZoomIn={viewport.zoomIn}
				onZoomOut={viewport.zoomOut}
				onFit={viewport.fit}
			/>

			<View
				style={{
					position: 'absolute',
					left: 12,
					bottom: 12,
					background: token.colorBgElevated,
					padding: '6px 10px',
					borderRadius: 4,
					boxShadow: token.boxShadowSecondary,
				}}
			>
				<View size={11} color={token.colorTextSecondary}>
					Molette : zoom · glisser : déplacer · clic sur une forme : la garder ou l’écarter
				</View>
			</View>
		</View>
	);
};

export default PlanShapesCanvas;
